/**
 * Doserne first-login onboarding — 3 steps:
 *   1. Language
 *   2. Menu side
 *   3. Short profile (full name + phone — both required)
 * Shown only when user.doserne_onboarding_completed !== true.
 */
import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, ArrowRight, Sparkles, Menu, Shield, User as UserIcon, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLang, LANGS } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 3;

export default function DoserneOnboarding({ user, authHeader, onDone }) {
  const { lang, setLang, menuSide, setMenuSide, t } = useLang();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [phone, setPhone]       = useState(user?.phone || "");

  const trimmedName = fullName.trim();
  const trimmedPhone = phone.trim();
  const canFinish = trimmedName.length >= 2 && trimmedPhone.length >= 3;

  const finish = async () => {
    if (!canFinish) {
      toast.error("Completează numele și telefonul");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/doserne/prefs`, {
        lang,
        menu_side: menuSide,
        full_name: trimmedName,
        phone: trimmedPhone,
        onboarding_completed: true,
      }, authHeader());
    } catch { /* silent */ }
    setSaving(false);
    onDone();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="onboarding-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[95vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-5 sm:rounded-t-3xl" style={{ color: "#fff" }}>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-cyan-400" strokeWidth={2.5} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Doserne Academy</div>
              <div className="text-lg font-black leading-tight">{t("onboard.welcome")}</div>
            </div>
          </div>
          <p className="text-xs text-slate-300">{t("onboard.step_intro")}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1 rounded-full transition-all ${s <= step ? "bg-cyan-400 w-8" : "bg-slate-700 w-4"}`} />
            ))}
          </div>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Steg 1 / {TOTAL_STEPS}</div>
              <h2 className="text-2xl font-black text-slate-900 leading-tight mb-1">{t("onboard.step1_title")}</h2>
              <p className="text-xs text-slate-500 mb-4">{t("onboard.step1_hint")}</p>

              <div className="space-y-2 mb-6">
                {LANGS.map((L) => {
                  const active = lang === L.code;
                  return (
                    <button
                      key={L.code}
                      onClick={() => setLang(L.code)}
                      type="button"
                      className={`w-full flex items-center gap-3 px-4 py-3 border-2 rounded-xl transition ${active ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`onboard-lang-${L.code}`}
                    >
                      <span className="text-2xl">{L.flag}</span>
                      <span className={`flex-1 text-left text-sm font-bold ${active ? "text-slate-900" : "text-slate-700"}`}>{L.label}</span>
                      {active && <CheckCircle2 className="w-5 h-5 text-cyan-600" />}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2"
                style={{ color: "#fff" }}
                data-testid="onboard-next-1"
              >
                {t("common.next")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Steg 2 / {TOTAL_STEPS}</div>
              <h2 className="text-2xl font-black text-slate-900 leading-tight mb-1">{t("onboard.step2_title")}</h2>
              <p className="text-xs text-slate-500 mb-4">{t("onboard.step2_hint")}</p>

              <div className="grid grid-cols-2 gap-2 mb-6">
                {[
                  { key: "left",  label: t("onboard.step2_left"),  align: "start" },
                  { key: "right", label: t("onboard.step2_right"), align: "end" },
                ].map((opt) => {
                  const active = menuSide === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setMenuSide(opt.key)}
                      type="button"
                      className={`p-4 border-2 rounded-xl transition ${active ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`onboard-side-${opt.key}`}
                    >
                      <div className={`aspect-video bg-slate-100 rounded-lg mb-2 flex items-${opt.align} p-1.5`}>
                        <div className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center" style={{ color: "#fff" }}>
                          <Menu className="w-3 h-3" />
                        </div>
                      </div>
                      <div className={`text-sm font-bold ${active ? "text-slate-900" : "text-slate-700"}`}>{opt.label}</div>
                      {active && <CheckCircle2 className="w-4 h-4 text-cyan-600 mt-1" />}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setStep(3)}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2"
                style={{ color: "#fff" }}
                data-testid="onboard-next-2"
              >
                {t("common.next")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Steg 3 / {TOTAL_STEPS}</div>
              <h2 className="text-2xl font-black text-slate-900 leading-tight mb-1">{t("onboard.step3_title")}</h2>
              <p className="text-xs text-slate-500 mb-4">{t("onboard.step3_hint")}</p>

              <div className="space-y-3 mb-6">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-1">
                    <UserIcon className="w-3 h-3" /> {t("onboard.step3_name")} <span className="text-red-500">*</span>
                  </span>
                  <input
                    autoFocus
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Elvis Popescu"
                    className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm text-slate-900 bg-white focus:outline-none focus:border-cyan-500"
                    data-testid="onboard-name-input"
                  />
                </label>

                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-1">
                    <Phone className="w-3 h-3" /> {t("onboard.step3_phone")} <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+47 900 00 000"
                    className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm text-slate-900 bg-white focus:outline-none focus:border-cyan-500"
                    data-testid="onboard-phone-input"
                  />
                </label>

                <div className="text-[10px] text-slate-400">
                  {t("onboard.step3_note")}
                </div>
              </div>

              <button
                onClick={finish}
                disabled={saving || !canFinish}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2"
                style={{ color: "#fff" }}
                data-testid="onboard-finish"
              >
                <Sparkles className="w-4 h-4" /> {t("onboard.finish")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
